import {
  HttpErrorResponse,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  type HttpEvent,
  type HttpInterceptor,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { FeatureStatsCollectorService } from './feature-stats-collector.service';
import type { StatBatch, StatEvent } from './feature-stats-collector.types';

// TODO: do we use it anywhere else?
@Injectable()
export class StatsCollectorInterceptor implements HttpInterceptor {
  private readonly _service = inject(FeatureStatsCollectorService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const isEnabled = this._service.isEnabled();
    const isStatsReq = this._isStatsRequest(req);

    // Only intercept if recording is enabled and it's a stats API call
    if (!isEnabled || !isStatsReq) {
      return next.handle(req);
    }

    const correlationId = uuid();
    return next.handle(req).pipe(
      tap({
        // Capture successful responses
        next: (response: HttpEvent<unknown>) => {
          if (response instanceof HttpResponse) {
            this._captureStatsBatch(
              req,
              response.status,
              req.body as StatEvent[],
              correlationId
            );
          }
        },
        // Capture error responses
        error: (response) => {
          if (response instanceof HttpErrorResponse) {
            this._captureStatsBatch(
              req,
              response.status,
              req.body as StatEvent[],
              correlationId,
              response.message
            );
          }
        },
      })
    );
  }

  /** Matches URLs belonging to the stats/analytics endpoints (stash service). */
  private readonly _statsUrlPattern = /\/stats\/|\/asianalytics\/|stash\./;

  /** Returns `true` if the request is a POST to a stats/analytics endpoint. */
  private _isStatsRequest(req: HttpRequest<unknown>): boolean {
    return req.method === 'POST' && this._statsUrlPattern.test(req.url);
  }

  private _captureStatsBatch(
    req: HttpRequest<unknown>,
    statusCode: number,
    requestBody: StatEvent[] | unknown,
    correlationId: string,
    error?: string
  ): void {
    // Parse the batch from the request body
    const batch = Array.isArray(requestBody) ? requestBody : [];

    const statBatch: StatBatch = {
      size: batch.length,
      request: batch as StatEvent[],
      timestamp: new Date().toISOString(),
      requestUrl: req.url,
      statusCode,
      correlationId,
      error,
    };

    this._service.appendStatBatch(statBatch);
  }
}
